import type * as React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/atoms/card";
import { cn } from "@/lib/utils";

// ─── Root ────────────────────────────────────────────────────────────────────

export type AuthTemplateProps = React.ComponentProps<"div">;

export function AuthTemplate({ className, ...props }: AuthTemplateProps) {
  return (
    <div
      className={cn(
        "relative flex min-h-screen items-center justify-center overflow-hidden p-6",
        className
      )}
      {...props}
    />
  );
}

// ─── Logo ────────────────────────────────────────────────────────────────────

export type AuthTemplateLogoProps = React.ComponentProps<"div">;

export function AuthTemplateLogo({
  className,
  ...props
}: AuthTemplateLogoProps) {
  return (
    <div
      className={cn(
        "absolute inset-x-0 top-20 z-10 flex justify-center",
        className
      )}
      {...props}
    />
  );
}

// ─── Card ────────────────────────────────────────────────────────────────────

export type AuthTemplateCardProps = React.ComponentProps<"div">;

export function AuthTemplateCard({
  className,
  ...props
}: AuthTemplateCardProps) {
  return (
    <Card
      className={cn(
        "z-20 w-full max-w-md gap-2 rounded-2xl border-border/50 bg-card pb-0 shadow-2xl lg:max-w-lg",
        className
      )}
      {...props}
    />
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

export type AuthTemplateHeaderProps = React.ComponentProps<"div">;

export function AuthTemplateHeader({
  className,
  ...props
}: AuthTemplateHeaderProps) {
  return <CardHeader className={cn("space-y-3 pb-6", className)} {...props} />;
}

// ─── Title ───────────────────────────────────────────────────────────────────

export type AuthTemplateTitleProps = React.ComponentProps<"div">;

export function AuthTemplateTitle({
  className,
  ...props
}: AuthTemplateTitleProps) {
  return (
    <CardTitle
      className={cn(
        "text-center font-bold font-serif text-3xl text-zinc-700 tracking-normal dark:text-zinc-300",
        className
      )}
      {...props}
    />
  );
}

// ─── Description ─────────────────────────────────────────────────────────────

export type AuthTemplateDescriptionProps = React.ComponentProps<"div">;

export function AuthTemplateDescription({
  className,
  ...props
}: AuthTemplateDescriptionProps) {
  return (
    <CardDescription
      className={cn("text-center text-base", className)}
      {...props}
    />
  );
}

// ─── Content ─────────────────────────────────────────────────────────────────

export type AuthTemplateContentProps = React.ComponentProps<"div">;

export function AuthTemplateContent({
  className,
  ...props
}: AuthTemplateContentProps) {
  return <CardContent className={cn("px-8 pb-6", className)} {...props} />;
}

// ─── Footer ──────────────────────────────────────────────────────────────────

export type AuthTemplateFooterProps = React.ComponentProps<"div">;

export function AuthTemplateFooter({
  className,
  ...props
}: AuthTemplateFooterProps) {
  return (
    <CardFooter
      className={cn(
        "flex flex-col rounded-b-2xl border-border/40 border-t bg-muted/20 py-6",
        className
      )}
      {...props}
    />
  );
}

// ─── Footer · Prompt ─────────────────────────────────────────────────────────

export type AuthTemplatePromptProps = React.ComponentProps<"div">;

export function AuthTemplatePrompt({
  className,
  ...props
}: AuthTemplatePromptProps) {
  return <div className={cn("mb-4 text-center", className)} {...props} />;
}

// ─── Footer · Prompt Link ────────────────────────────────────────────────────

export type AuthTemplatePromptLinkProps = React.ComponentProps<"a">;

export function AuthTemplatePromptLink({
  className,
  ...props
}: AuthTemplatePromptLinkProps) {
  return (
    <a
      className={cn(
        "font-medium text-brand-text text-sm transition-colors hover:underline",
        className
      )}
      {...props}
    />
  );
}

// ─── Footer · Legal ──────────────────────────────────────────────────────────

export type AuthTemplateLegalProps = React.ComponentProps<"p">;

export function AuthTemplateLegal({
  className,
  ...props
}: AuthTemplateLegalProps) {
  return (
    <p
      className={cn(
        "px-6 text-center text-muted-foreground text-xs leading-relaxed",
        className
      )}
      {...props}
    />
  );
}

// ─── Footer · Legal Link ─────────────────────────────────────────────────────

export type AuthTemplateLegalLinkProps = React.ComponentProps<"a">;

export function AuthTemplateLegalLink({
  className,
  ...props
}: AuthTemplateLegalLinkProps) {
  return (
    <a
      className={cn(
        "font-medium text-brand-text transition-colors hover:underline",
        className
      )}
      {...props}
    />
  );
}

// ─── Default Background ─────────────────────────────────────────────────────

export type AuthTemplateDefaultBackgroundProps = React.ComponentProps<"div">;

export function AuthTemplateDefaultBackground({
  className,
  ...props
}: AuthTemplateDefaultBackgroundProps) {
  return (
    <div
      className={cn(
        "absolute inset-0 z-0 bg-gradient-to-br from-orange-400/40 via-pink-500/30 to-purple-600/40 blur-md",
        className
      )}
      {...props}
    />
  );
}

// ─── Video Background ───────────────────────────────────────────────────────

export type AuthTemplateVideoBackgroundProps = {
  /** Poster image shown while the video loads */
  poster?: string;
  /** Playback rate for the background video (default: 0.7) */
  playbackRate?: number;
  /** URL to the background video file */
  src: string;
  /** Class name for the video element */
  videoClassName?: string;
} & React.ComponentProps<"div">;

export function AuthTemplateVideoBackground({
  className,
  playbackRate = 0.7,
  poster,
  src,
  videoClassName,
  ...props
}: AuthTemplateVideoBackgroundProps) {
  return (
    <div
      className={cn("absolute -inset-4 z-0 motion-reduce:hidden", className)}
      {...props}
    >
      <video
        autoPlay
        className={cn(
          "size-full object-cover opacity-60 blur-lg brightness-50 contrast-110",
          videoClassName
        )}
        loop
        muted
        playsInline
        poster={poster}
        preload="none"
        ref={(el) => {
          if (el) {
            el.playbackRate = playbackRate;
          }
        }}
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
}

// ─── Grid Overlay ───────────────────────────────────────────────────────────

export type AuthTemplateGridOverlayProps = {
  /** URL to the grid SVG asset */
  gridUrl: string;
} & React.ComponentProps<"div">;

export function AuthTemplateGridOverlay({
  className,
  gridUrl,
  ...props
}: AuthTemplateGridOverlayProps) {
  return (
    <div
      className={cn("absolute inset-0 z-1 opacity-[0.01]", className)}
      style={{
        backgroundImage: `url(${gridUrl})`,
        backgroundPosition: "center center",
        backgroundRepeat: "no-repeat",
        backgroundSize: "100% 100%",
      }}
      {...props}
    />
  );
}
